import {
  ChevronRight,
  Close,
  Copy,
  Edit,
  EmojiEvent,
  Lock,
  Logout,
  Security,
  SettingsGear,
  Share,
  Support,
  UserIconFilled,
} from '@/components/icons'
import { SUPPORT_DIRECT } from '@/constants/Common'
import { clearAuthCookies } from '@/utils/authCookies'
import {
  ROUTE_LOGIN,
  ROUTE_PASSWORD_SECURITY,
  ROUTE_PREFERENCES,
  ROUTE_PRIVACY_POLICY,
  ROUTE_PROFILE,
  ROUTE_CONNECTIONS,
  ROUTE_SUBSCRIPTION,
  ROUTE_TERMS,
} from '@/constants/Routes'
import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { resetAuth } from '@/pages/auth/authSlice'
import { resetClub } from '@/pages/club/clubSlice'
import { resetEvent } from '@/pages/event/eventSlice'
import { resetPlayer } from '@/pages/player/playerSlice'
import { userSelector } from '@/pages/user/selectors'
import { resetUser } from '@/pages/user/userSlice'
import { logout } from '@/services'
import { queryClient } from '@/queryClient'
import { User } from '@/types'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { useCopyToClipboard } from 'usehooks-ts'
import LanguagesAccordion from '../accordion/LanguagesAccordion'
import { Button, ShareButton } from '../button'
import { Container } from './Container'
import { SubscriptionIcon } from '../icons/SubscriptionIcon'

type Props = {
  dropdownClass?: string
  isDropdown?: boolean
  openDropdown?: boolean
  user: User
  onCloseModal?: () => void
  onRunEvent?: () => void
}

export const DropdownProfile = ({ isDropdown, openDropdown, user, onCloseModal, dropdownClass, onRunEvent }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { chatClient } = useAppSelector(userSelector)

  const shareText = 'Hey, have a look at my DUPR Profile'

  const BASE_URL = window.location.origin

  const [value, copy] = useCopyToClipboard()

  const handleLogOut = () => {
    clearAuthCookies()
    logout()
    chatClient?.disconnectUser()
    dispatch(resetAuth())
    dispatch(resetUser())
    dispatch(resetEvent())
    dispatch(resetPlayer())
    dispatch(resetClub())
    queryClient.clear()
    navigate(ROUTE_LOGIN)
    if (onCloseModal) onCloseModal()
  }

  return (
    <Container isDropdown={isDropdown} openDropdown={openDropdown} dropdownClass={dropdownClass}>
      {!isDropdown && (
        <div className="flex justify-end items-end">
          <Button onClick={onCloseModal} className="p-3 hover:opacity-50">
            <Close />
          </Button>
        </div>
      )}
      <div className={`pl-5 ${isDropdown && 'pt-8'} pr-3 flex flex-row gap-x-4 pb-8`}>
        {user?.imageUrl ? (
          <img src={user?.imageUrl} alt="" className="w-16 h-16 rounded-full" />
        ) : (
          <UserIconFilled className="w-16 h-16 text-primary-500" />
        )}

        <div className="flex flex-col">
          <span className="text-navy-900 font-semibold text-xl">{user?.fullName}</span>
          <span className="text-navy-900 text-opacity-50 text-sm">
            {user?.addresses[0]?.shortAddress} • {user?.gender ? t(`player.${user?.gender}`) : ''}
          </span>
          <div className="flex py-2 flex-row gap-x-4">
            <ShareButton
              className="!bg-navy-900 !text-navy-900 !bg-opacity-5 hover:!bg-opacity-10"
              iconClass="!text-navy-900"
              shareUrl={`${BASE_URL}/dashboard/player/${user.id}`}
              shareText={shareText}
              pageName={TRACKING_TYPES.USER_PROFILE}
              shareContent={TRACKING_TYPES.PROFILE_SHARE}
            />
            <div className="relative">
              <button
                className="flex items-center py-2 px-4 rounded-full gap-x-1 text-navy-900 text-sm font-medium my-dupr-id"
                onClick={() => copy(user?.referralCode)}
              >
                ID: {user?.referralCode}
                <Copy className="text-navy-900" />
                <Tooltip anchorSelect=".my-dupr-id" place="bottom" className="text-navy-900">
                  {value ? t('common.DUPRIDCopied') : t('common.copyClipboard')}
                </Tooltip>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h4 className="px-4 py-2 bg-navy-900 bg-opacity-5 text-sm text-navy-900 text-opacity-50 font-medium">
          {t('common.myAccount')}
        </h4>
        <div className="">
          <Link
            to={ROUTE_PROFILE}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <Edit />
            <span className="flex-1 text-sm font-semibold">{t('common.editProfile')}</span>
            <ChevronRight />
          </Link>
          <Link
            to={ROUTE_PREFERENCES}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <SettingsGear />
            <span className="flex-1 text-sm font-semibold">Preferences</span>
            <ChevronRight />
          </Link>
          <Link
            to={ROUTE_SUBSCRIPTION}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <SubscriptionIcon />
            <span className="flex-1 text-sm font-semibold">{t('common.subscriptions')}</span>
            <ChevronRight />
          </Link>
          <Link
            to={ROUTE_CONNECTIONS}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <Share />
            <span className="flex-1 text-sm font-semibold">Connected Apps</span>
            <ChevronRight />
          </Link>
          <Link
            to={ROUTE_PASSWORD_SECURITY}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <Lock />
            <span className="flex-1 text-sm font-semibold">{t('common.passwordSecurity')}</span>
            <ChevronRight />
          </Link>
          <LanguagesAccordion />
        </div>

        <h4 className="px-4 py-2 bg-navy-900 bg-opacity-5 text-sm text-navy-900 text-opacity-50 font-medium">
          {t('common.infoSupport')}
        </h4>
        <div className="">
          <Link
            to={SUPPORT_DIRECT}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
            target="_blank"
          >
            <Support />
            <span className="flex-1 text-sm font-semibold">{t('common.support')}</span>
            <ChevronRight />
          </Link>
          <div
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2 cursor-pointer"
            onClick={() => {
              if (onRunEvent) {
                onRunEvent()
              }
            }}
          >
            <EmojiEvent />
            <span className="flex-1 text-sm font-semibold">{t('common.runEvents')}</span>
            <ChevronRight />
          </div>
          <Link
            to={'https://www.dupr.com/community-guidelines'}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
            target="_blank"
          >
            <Security />
            <span className="flex-1 text-sm font-semibold">{t('common.communityGuidelines')}</span>
            <ChevronRight />
          </Link>
          <Link
            to={ROUTE_TERMS}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <Security />
            <span className="flex-1 text-sm font-semibold">{t('common.termsUse')}</span>
            <ChevronRight />
          </Link>
          <Link
            to={ROUTE_PRIVACY_POLICY}
            className="text-navy-900 flex gap-x-4 py-4 items-center hover:cursor-pointer hover:bg-slate-100 pl-4 pr-2"
          >
            <Security />
            <span className="flex-1 text-sm font-semibold">{t('common.privacyPolicy')}</span>
            <ChevronRight />
          </Link>
        </div>
      </div>

      <div
        className="mt-4 p-4 text-boldOrange flex gap-x-4 hover:cursor-pointer hover:bg-slate-100"
        onClick={handleLogOut}
      >
        <Logout />
        <span className="font-semibold">{t('common.logout')}</span>
      </div>
    </Container>
  )
}
